import React, { useContext, useState } from 'react';
import { StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { Text } from '..';
import { ThemeContext } from '../ThemeProvider';

interface SwitchButton {
  label: string;
  [key: string]: any;
}

export interface SwitchButtonProps {
  buttons: SwitchButton[];
  defaultIndex?: number;
  onChange?: Function;
  style?: ViewStyle;
  variant?: 'default' | 'big';
}

const getStyles = ({ colors }: Theme) => {
  const DEFAULT_COLOR = colors.white;
  const ACTIVE_COLOR = colors.primary.normal;
  const BORDER_RADIUS = 5;

  return StyleSheet.create({
    container: {
      backgroundColor: colors.transparent,
      flexDirection: 'row',
    },
    defaultVariantContainer: {
      height: 25,
    },
    bigVariantContainer: {
      height: 100,
      shadowColor: 'grey',
      shadowOpacity: 0.7,
      shadowOffset: {
        height: 1,
        width: 0,
      },
      elevation: 2,
    },
    button: {
      flex: 1,
      backgroundColor: DEFAULT_COLOR,
      alignItems: 'center',
      justifyContent: 'center',
    },
    defaultVariantButton: {
      borderColor: ACTIVE_COLOR,
      borderWidth: 1,
    },
    bigVariantButton: {
      paddingHorizontal: 10,
    },
    activeButton: {
      backgroundColor: ACTIVE_COLOR,
    },
    buttonText: {
      color: ACTIVE_COLOR,
      textAlign: 'center',
    },
    activeButtonText: {
      color: DEFAULT_COLOR,
    },
    leftButton: {
      borderTopLeftRadius: BORDER_RADIUS,
      borderBottomLeftRadius: BORDER_RADIUS,
    },
    rightButton: {
      borderTopRightRadius: BORDER_RADIUS,
      borderBottomRightRadius: BORDER_RADIUS,
    },
  });
};

export const SwitchButton = ({
  buttons,
  defaultIndex = 0,
  onChange,
  style,
  variant = 'default',
}: SwitchButtonProps) => {
  const theme = useContext(ThemeContext);
  const styles = getStyles(theme);

  const variantStylesMap = {
    big: {
      button: styles.bigVariantButton,
      container: styles.bigVariantContainer,
    },
    default: {
      button: styles.defaultVariantButton,
      container: styles.defaultVariantContainer,
    },
  };

  const [activeIndex, setActiveIndex] = useState(defaultIndex);

  const isActive = (index: number) => activeIndex === index;

  const isLeftButton = (index: number) => index === 0;
  const isRightButton = (index: number) => index === buttons.length - 1;

  const selectButton = (button: SwitchButton, index: number) => {
    if (isActive(index)) {
      return;
    }

    if (onChange) onChange(button);

    setActiveIndex(index);
  };

  return (
    <View style={[style, styles.container, variantStylesMap[variant].container]}>
      {buttons.map((button, index) => (
        <TouchableOpacity
          key={index}
          style={[
            styles.button,
            variantStylesMap[variant].button,
            isActive(index) ? styles.activeButton : null,
            variant !== 'big' && isLeftButton(index) ? styles.leftButton : null,
            variant !== 'big' && isRightButton(index) ? styles.rightButton : null,
          ]}
          onPress={() => selectButton(button, index)}
        >
          <Text style={[styles.buttonText, isActive(index) ? styles.activeButtonText : null]}>{button.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};
